import React from "react";
import SectionCard from "../components/SectionCard";
import {
  BilingualTableHeader,
  BilingualTextRow,
  BilingualImageRow,
  BilingualRepeaterRow,
} from "../components/BilingualFormFields";
import ReadOnlyBadge from "../components/ReadOnlyBadge";
import { getError } from "../utils/validation";
import { asImageAsset } from "../utils/cmsImageAsset";
import { countSectionErrors, HexFeaturesSection } from "./SharedCmsSections";

function getProductDetail(product) {
  if (product.detail) return product.detail;
  return {
    title: product.title || "",
    image: product.image ? { ...product.image } : { src: "", alt: "" },
    paragraphs: product.description ? [product.description] : [""],
    specifications: [],
  };
}

function SmartSolutionsSection({
  solutionsEn,
  solutionsEs,
  setSolutionsEn,
  setSolutionsEs,
  errors,
  onImageUpload,
  uploadingKey,
}) {
  const smartEn = solutionsEn.smartSolutions || {};
  const smartEs = solutionsEs.smartSolutions || {};
  const itemsEn = smartEn.items || [];
  const itemsEs = smartEs.items || [];
  const sectionIconEn = asImageAsset(smartEn.sectionIcon);
  const sectionIconEs = asImageAsset(smartEs.sectionIcon);

  const setSmartEn = (patch) =>
    setSolutionsEn({ ...solutionsEn, smartSolutions: { ...smartEn, ...patch } });
  const setSmartEs = (patch) =>
    setSolutionsEs({ ...solutionsEs, smartSolutions: { ...smartEs, ...patch } });

  const updateItemEn = (index, patch) => {
    const next = itemsEn.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    setSmartEn({ items: next });
  };
  const updateItemEs = (index, patch) => {
    const next = itemsEs.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    setSmartEs({ items: next });
  };

  return (
    <SectionCard
      id="smartSolutions"
      title="Smart solutions"
      errorCount={countSectionErrors(errors, ["smartSolutions."])}
      collapsible
    >
      <BilingualTextRow
        label="Section title"
        valueEn={smartEn.sectionTitle}
        valueEs={smartEs.sectionTitle}
        onChangeEn={(v) => setSmartEn({ sectionTitle: v })}
        onChangeEs={(v) => setSmartEs({ sectionTitle: v })}
        errorEn={getError("smartSolutions.sectionTitle", errors, "en")}
        errorEs={getError("smartSolutions.sectionTitle", errors, "es")}
      />
      <BilingualImageRow
        label="Section icon"
        fieldKey="smartSolutions.sectionIcon"
        srcEn={sectionIconEn.src}
        altEn={sectionIconEn.alt}
        srcEs={sectionIconEs.src}
        altEs={sectionIconEs.alt}
        onSrcEn={(v) => setSmartEn({ sectionIcon: { ...sectionIconEn, src: v } })}
        onAltEn={(v) => setSmartEn({ sectionIcon: { ...sectionIconEn, alt: v } })}
        onSrcEs={(v) => setSmartEs({ sectionIcon: { ...sectionIconEs, src: v } })}
        onAltEs={(v) => setSmartEs({ sectionIcon: { ...sectionIconEs, alt: v } })}
        onUploadEn={(f) =>
          onImageUpload("en", "smart.icon", f, (url) =>
            setSmartEn({ sectionIcon: { ...sectionIconEn, src: url } })
          )
        }
        onUploadEs={(f) =>
          onImageUpload("es", "smart.icon", f, (url) =>
            setSmartEs({ sectionIcon: { ...sectionIconEs, src: url } })
          )
        }
        srcErrorEn={getError("smartSolutions.sectionIcon.src", errors, "en")}
        altErrorEn={getError("smartSolutions.sectionIcon.alt", errors, "en")}
        srcErrorEs={getError("smartSolutions.sectionIcon.src", errors, "es")}
        altErrorEs={getError("smartSolutions.sectionIcon.alt", errors, "es")}
        uploadingEn={uploadingKey === "en.smart.icon"}
        uploadingEs={uploadingKey === "es.smart.icon"}
      />
      {itemsEn.map((item, index) => (
        <div key={item.id} className="cms-bilingual-block">
          <ReadOnlyBadge id={item.id} />
          <BilingualTextRow
            label="Title"
            valueEn={item.title}
            valueEs={(itemsEs[index] && itemsEs[index].title) || ""}
            onChangeEn={(v) => updateItemEn(index, { title: v })}
            onChangeEs={(v) => updateItemEs(index, { title: v })}
            errorEn={getError(`smartSolutions.items.${index}.title`, errors, "en")}
            errorEs={getError(`smartSolutions.items.${index}.title`, errors, "es")}
          />
          <BilingualTextRow
            label="Description"
            valueEn={item.description}
            valueEs={(itemsEs[index] && itemsEs[index].description) || ""}
            onChangeEn={(v) => updateItemEn(index, { description: v })}
            onChangeEs={(v) => updateItemEs(index, { description: v })}
            multiline
            rows={2}
            errorEn={getError(`smartSolutions.items.${index}.description`, errors, "en")}
            errorEs={getError(`smartSolutions.items.${index}.description`, errors, "es")}
          />
          <BilingualImageRow
            label="Icon"
            srcEn={item.icon && item.icon.src}
            altEn={item.icon && item.icon.alt}
            srcEs={itemsEs[index] && itemsEs[index].icon && itemsEs[index].icon.src}
            altEs={itemsEs[index] && itemsEs[index].icon && itemsEs[index].icon.alt}
            onSrcEn={(v) => updateItemEn(index, { icon: { ...item.icon, src: v } })}
            onAltEn={(v) => updateItemEn(index, { icon: { ...item.icon, alt: v } })}
            onSrcEs={(v) =>
              updateItemEs(index, {
                icon: { ...(itemsEs[index] && itemsEs[index].icon), src: v },
              })
            }
            onAltEs={(v) =>
              updateItemEs(index, {
                icon: { ...(itemsEs[index] && itemsEs[index].icon), alt: v },
              })
            }
            onUploadEn={(f) =>
              onImageUpload("en", `smart.${index}`, f, (url) =>
                updateItemEn(index, { icon: { ...item.icon, src: url } })
              )
            }
            onUploadEs={(f) =>
              onImageUpload("es", `smart.${index}`, f, (url) =>
                updateItemEs(index, {
                  icon: { ...(itemsEs[index] && itemsEs[index].icon), src: url },
                })
              )
            }
            srcErrorEn={getError(`smartSolutions.items.${index}.icon.src`, errors, "en")}
            altErrorEn={getError(`smartSolutions.items.${index}.icon.alt`, errors, "en")}
            srcErrorEs={getError(`smartSolutions.items.${index}.icon.src`, errors, "es")}
            altErrorEs={getError(`smartSolutions.items.${index}.icon.alt`, errors, "es")}
            uploadingEn={uploadingKey === `en.smart.${index}`}
            uploadingEs={uploadingKey === `es.smart.${index}`}
          />
        </div>
      ))}
    </SectionCard>
  );
}

function IntegratedDevicesSection({
  solutionsEn,
  solutionsEs,
  setSolutionsEn,
  setSolutionsEs,
  errors,
  onImageUpload,
  uploadingKey,
}) {
  const devEn = solutionsEn.integratedDevices || {};
  const devEs = solutionsEs.integratedDevices || {};
  const productsEn = devEn.products || [];
  const productsEs = devEs.products || [];

  const setDevEn = (patch) =>
    setSolutionsEn({ ...solutionsEn, integratedDevices: { ...devEn, ...patch } });
  const setDevEs = (patch) =>
    setSolutionsEs({ ...solutionsEs, integratedDevices: { ...devEs, ...patch } });

  const updateProductEn = (index, patch) => {
    const next = productsEn.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    setDevEn({ products: next });
  };
  const updateProductEs = (index, patch) => {
    const next = productsEs.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    setDevEs({ products: next });
  };

  return (
    <SectionCard
      id="integratedDevices"
      title="Integrated devices"
      errorCount={countSectionErrors(errors, ["integratedDevices."])}
      collapsible
    >
      <BilingualTextRow
        label="Section title"
        valueEn={devEn.sectionTitle}
        valueEs={devEs.sectionTitle}
        onChangeEn={(v) => setDevEn({ sectionTitle: v })}
        onChangeEs={(v) => setDevEs({ sectionTitle: v })}
        errorEn={getError("integratedDevices.sectionTitle", errors, "en")}
        errorEs={getError("integratedDevices.sectionTitle", errors, "es")}
      />
      <BilingualTextRow
        label="Description"
        valueEn={devEn.description}
        valueEs={devEs.description}
        onChangeEn={(v) => setDevEn({ description: v })}
        onChangeEs={(v) => setDevEs({ description: v })}
        multiline
        rows={2}
        errorEn={getError("integratedDevices.description", errors, "en")}
        errorEs={getError("integratedDevices.description", errors, "es")}
      />
      <BilingualImageRow
        label="Specification check icon"
        fieldKey="integratedDevices.specCheckIcon"
        srcEn={devEn.specCheckIcon}
        altEn=""
        srcEs={devEs.specCheckIcon}
        altEs=""
        onSrcEn={(v) => setDevEn({ specCheckIcon: v })}
        onAltEn={() => {}}
        onSrcEs={(v) => setDevEs({ specCheckIcon: v })}
        onAltEs={() => {}}
        onUploadEn={(f) =>
          onImageUpload("en", "dev.specIcon", f, (url) => setDevEn({ specCheckIcon: url }))
        }
        onUploadEs={(f) =>
          onImageUpload("es", "dev.specIcon", f, (url) => setDevEs({ specCheckIcon: url }))
        }
        uploadingEn={uploadingKey === "en.dev.specIcon"}
        uploadingEs={uploadingKey === "es.dev.specIcon"}
      />
      {productsEn.map((product, index) => {
        const productEs = productsEs[index] || {};
        const detailEn = getProductDetail(product);
        const detailEs = getProductDetail(productEs.id ? productEs : product);

        const updateDetailEn = (patch) => {
          const current = productsEn[index] || product;
          updateProductEn(index, {
            detail: { ...getProductDetail(current), ...patch },
          });
        };
        const updateDetailEs = (patch) => {
          const current = productsEs[index] || product;
          updateProductEs(index, {
            detail: { ...getProductDetail(current), ...patch },
          });
        };

        const specLabelsEn = (detailEn.specifications || []).map((s) => s.label || "");
        const specLabelsEs = (detailEs.specifications || []).map((s) => s.label || "");

        return (
        <div key={product.id} className="cms-bilingual-block">
          <ReadOnlyBadge id={product.id} />
          <BilingualTextRow
            label="Title"
            valueEn={product.title}
            valueEs={(productsEs[index] && productsEs[index].title) || ""}
            onChangeEn={(v) => updateProductEn(index, { title: v })}
            onChangeEs={(v) => updateProductEs(index, { title: v })}
            errorEn={getError(`integratedDevices.products.${index}.title`, errors, "en")}
            errorEs={getError(`integratedDevices.products.${index}.title`, errors, "es")}
          />
          <BilingualTextRow
            label="Card description"
            valueEn={product.description}
            valueEs={(productsEs[index] && productsEs[index].description) || ""}
            onChangeEn={(v) => updateProductEn(index, { description: v })}
            onChangeEs={(v) => updateProductEs(index, { description: v })}
            multiline
            rows={3}
            errorEn={getError(`integratedDevices.products.${index}.description`, errors, "en")}
            errorEs={getError(`integratedDevices.products.${index}.description`, errors, "es")}
          />
          <BilingualImageRow
            label="Product image"
            srcEn={product.image && product.image.src}
            altEn={product.image && product.image.alt}
            srcEs={productsEs[index] && productsEs[index].image && productsEs[index].image.src}
            altEs={productsEs[index] && productsEs[index].image && productsEs[index].image.alt}
            onSrcEn={(v) => updateProductEn(index, { image: { ...product.image, src: v } })}
            onAltEn={(v) => updateProductEn(index, { image: { ...product.image, alt: v } })}
            onSrcEs={(v) =>
              updateProductEs(index, {
                image: { ...(productsEs[index] && productsEs[index].image), src: v },
              })
            }
            onAltEs={(v) =>
              updateProductEs(index, {
                image: { ...(productsEs[index] && productsEs[index].image), alt: v },
              })
            }
            onUploadEn={(f) =>
              onImageUpload("en", `dev.${index}`, f, (url) =>
                updateProductEn(index, { image: { ...product.image, src: url } })
              )
            }
            onUploadEs={(f) =>
              onImageUpload("es", `dev.${index}`, f, (url) =>
                updateProductEs(index, {
                  image: { ...(productsEs[index] && productsEs[index].image), src: url },
                })
              )
            }
            srcErrorEn={getError(`integratedDevices.products.${index}.image.src`, errors, "en")}
            altErrorEn={getError(`integratedDevices.products.${index}.image.alt`, errors, "en")}
            srcErrorEs={getError(`integratedDevices.products.${index}.image.src`, errors, "es")}
            altErrorEs={getError(`integratedDevices.products.${index}.image.alt`, errors, "es")}
            uploadingEn={uploadingKey === `en.dev.${index}`}
            uploadingEs={uploadingKey === `es.dev.${index}`}
          />

          <p className="cms-inline-block-title">Popup — Learn more modal</p>
          <BilingualImageRow
            label="Popup hero image"
            fieldKey={`integratedDevices.products.${index}.detail.image`}
            srcEn={detailEn.image && detailEn.image.src}
            altEn={detailEn.image && detailEn.image.alt}
            srcEs={detailEs.image && detailEs.image.src}
            altEs={detailEs.image && detailEs.image.alt}
            onSrcEn={(v) => updateDetailEn({ image: { ...detailEn.image, src: v } })}
            onAltEn={(v) => updateDetailEn({ image: { ...detailEn.image, alt: v } })}
            onSrcEs={(v) => updateDetailEs({ image: { ...detailEs.image, src: v } })}
            onAltEs={(v) => updateDetailEs({ image: { ...detailEs.image, alt: v } })}
            onUploadEn={(f) =>
              onImageUpload("en", `dev.${index}.detail`, f, (url) =>
                updateDetailEn({ image: { ...detailEn.image, src: url } })
              )
            }
            onUploadEs={(f) =>
              onImageUpload("es", `dev.${index}.detail`, f, (url) =>
                updateDetailEs({ image: { ...detailEs.image, src: url } })
              )
            }
            uploadingEn={uploadingKey === `en.dev.${index}.detail`}
            uploadingEs={uploadingKey === `es.dev.${index}.detail`}
          />
          <BilingualRepeaterRow
            label="Popup body paragraphs"
            fieldKey={`integratedDevices.products.${index}.detail.paragraphs`}
            pointsEn={detailEn.paragraphs || [""]}
            pointsEs={detailEs.paragraphs || [""]}
            onChangeEn={(paragraphs) => updateDetailEn({ paragraphs })}
            onChangeEs={(paragraphs) => updateDetailEs({ paragraphs })}
            minItems={1}
          />
          <BilingualRepeaterRow
            label="Specification bullets"
            fieldKey={`integratedDevices.products.${index}.detail.specifications`}
            pointsEn={specLabelsEn.length ? specLabelsEn : [""]}
            pointsEs={specLabelsEs.length ? specLabelsEs : [""]}
            onChangeEn={(labels) =>
              updateDetailEn({
                specifications: (labels || []).map((label) => ({ label: label || "" })),
              })
            }
            onChangeEs={(labels) =>
              updateDetailEs({
                specifications: (labels || []).map((label) => ({ label: label || "" })),
              })
            }
            minItems={0}
          />
        </div>
        );
      })}
    </SectionCard>
  );
}

function AlternatingFeaturesSection(props) {
  const { solutionsEn, solutionsEs, setSolutionsEn, setSolutionsEs } = props;
  return (
    <HexFeaturesSection
      {...props}
      sectionId="alternatingFeatures"
      featuresEn={solutionsEn.alternatingFeatures || []}
      featuresEs={solutionsEs.alternatingFeatures || []}
      setFeaturesEn={(features) => setSolutionsEn({ ...solutionsEn, alternatingFeatures: features })}
      setFeaturesEs={(features) => setSolutionsEs({ ...solutionsEs, alternatingFeatures: features })}
      prefix="alternatingFeatures"
    />
  );
}

export { SmartSolutionsSection, AlternatingFeaturesSection, IntegratedDevicesSection };
